import * as SwitchPrimitives from '@radix-ui/react-switch'
import { cva } from 'class-variance-authority'
import * as React from 'react'
import LoaderIcon from '~/src/components/svg/LoaderIcon'
import { cn } from '~/src/lib/utils'

export interface SwitchProps
  extends React.ComponentPropsWithoutRef<typeof SwitchPrimitives.Root> {
  icon?: React.ReactNode
  size?: 'sm' | 'md' | 'lg'
  variant?: 'default' | 'black' | 'pro' | 'success' | 'danger'
  thumbClassName?: string
  loading?: boolean
}

const switchVariants = cva(
  'peer inline-flex shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      variant: {
        default:
          'data-[state=unchecked]:bg-gray-300 data-[state=checked]:bg-slate-800',
        black:
          'data-[state=unchecked]:bg-gray-300 data-[state=checked]:bg-black',
        pro: 'data-[state=unchecked]:bg-gray-300 data-[state=checked]:bg-blue-200',
        success:
          'data-[state=unchecked]:bg-gray-300 data-[state=checked]:bg-green-500',
        danger:
          'data-[state=unchecked]:bg-gray-300 data-[state=checked]:bg-red-600',
      },
      size: {
        sm: 'h-[22px] w-[34px] md:h-6 md:w-11',
        md: 'h-7 w-12 md:h-8 md:w-14',
        lg: 'h-8 w-14 md:h-10 md:w-16',
      },
    },
    defaultVariants: {
      size: 'md',
      variant: 'default',
    },
  }
)

const thumbVariants = cva(
  'bg-white pointer-events-none flex items-center justify-center rounded-full shadow-lg ring-0 transition-transform',
  {
    variants: {
      size: {
        sm: 'h-[18px] w-[18px] data-[state=checked]:translate-x-3 data-[state=unchecked]:translate-x-0 md:h-5 md:w-5 md:data-[state=checked]:translate-x-5',
        md: 'h-6 w-6 data-[state=checked]:translate-x-5 data-[state=unchecked]:translate-x-0 md:h-7 md:w-7 md:data-[state=checked]:translate-x-6',
        lg: 'h-7 w-7 data-[state=checked]:translate-x-6 data-[state=unchecked]:translate-x-0 md:h-9 md:w-9 md:data-[state=checked]:translate-x-7',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  }
)

const Switch = React.forwardRef<
  React.ComponentRef<typeof SwitchPrimitives.Root>,
  SwitchProps
>(
  (
    {
      icon,
      className,
      thumbClassName,
      size = 'md',
      variant = 'default',
      loading,
      ...props
    },
    ref
  ) => (
    <SwitchPrimitives.Root
      className={cn(switchVariants({ size, variant }), className)}
      {...props}
      ref={ref}
    >
      <SwitchPrimitives.Thumb
        className={cn(thumbVariants({ size }), thumbClassName)}
      >
        {loading ? (
          <div className='animate-spin'>
            <LoaderIcon className='w-6 text-black md:w-7' />
          </div>
        ) : (
          icon
        )}
      </SwitchPrimitives.Thumb>
    </SwitchPrimitives.Root>
  )
)
Switch.displayName = SwitchPrimitives.Root.displayName

export { Switch }
